import { DateFormatter } from '@repo/toolkit/date'
import { StringFormatter } from '@repo/toolkit/string'
import { toFullName } from '@repo/toolkit/to-full-name'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import { LockIcon, UserCanceledIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { useMutation } from '@tanstack/react-query'
import React, { useRef } from 'react'

import { API, constructApiUrl } from '../../../api/apis.config'
import { createBlockedPlace } from '../../../api/blocked-places/create-blocked-place'
import { deleteBlockedPlaces } from '../../../api/blocked-places/delete-blocked-place'
import DynamicDataTable, { DynamicTableRef } from '../../../components/elements/dynamic-table'
import PeriodBadge from '../../../components/elements/period-badge'
import { BlockedPlaceForm } from '../../../components/pages/centers/blocked-places/blocked-place-form'
import { useObserver } from '../../../lib/contexts/observer'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import {
  BlockedPlaceFilters,
  BlockedPlaceLight
} from '../../../lib/types/models/blocked-place/blocked-place'
import { TripProperties } from '../../../lib/types/models/trip/properties.trip'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'

type Props = {
  centerId: number
  onUnblockStudent: () => void
  onReserveBlockedPlace: () => void
}

const CenterBlockedStudents: React.FC<Props> = ({
  centerId,
  onUnblockStudent,
  onReserveBlockedPlace
}) => {
  const tableRef = useRef<DynamicTableRef<BlockedPlaceLight>>(null)

  const subject = useObserver({
    key: 'blocked-places',
    update: () => tableRef.current?.refetch()
  })

  const { selectedData, modalState, toggleModal, closeModel } = useModalManager<
    'filters' | 'create-blocked-place' | 'delete-blocked-place',
    BlockedPlaceLight[]
  >(['filters', 'create-blocked-place', 'delete-blocked-place'])

  const { mutateAsync: createBlockedPlaceMutation } = useMutation({
    mutationFn: createBlockedPlace,
    onSuccess: () => {
      tableRef.current?.refetch()
      onReserveBlockedPlace()
      closeModel('create-blocked-place')
    }
  })

  const { mutateAsync: deleteBlockedPlacesMutation, isPending: isDeleteBlockedPlacesPending } =
    useMutation({
      mutationFn: deleteBlockedPlaces,
      onSuccess: () => {
        tableRef.current?.refetch()
        subject.notify(['affected-students'])
        onUnblockStudent()
        closeModel('delete-blocked-place')
      }
    })

  const selectedBlockedPlaceNames = (selectedData ?? [])
    .map((blockedPlace) =>
      toFullName(blockedPlace.firstName, blockedPlace.lastName, {
        options: { mode: 'lastname-firstname' }
      })
    )
    .join(', ')

  const columns: Column<BlockedPlaceLight>[] = [
    {
      accessorKey: 'journey.reference',
      header: 'Séjours',
      cell: ({ row }) => (
        <PeriodBadge
          period={row.original.journey?.date.period}
          size="small"
          text={row.original.journey?.reference}
        />
      )
    },
    {
      accessorKey: 'lastName',
      header: `NOM`,
      cell: ({ row }) => row.original.lastName.toUpperCase()
    },
    {
      accessorKey: 'firstName',
      header: 'Prénom',
      cell: ({ row }) =>
        row.original.firstName ? StringFormatter.capitalize(row.original.firstName) : '-'
    },
    {
      accessorKey: 'mainTrip',
      header: 'Voyage principal',
      cell: ({ row }) => TripProperties.getTripLabel(row.original.mainTrip)
    },
    {
      accessorKey: 'secondaryTrip',
      header: 'Voyage secondaire',
      cell: ({ row }) =>
        row.original.secondaryTrip ? TripProperties.getTripLabel(row.original.secondaryTrip) : '-'
    },
    {
      accessorKey: 'id',
      header: 'Ami',
      enableSorting: true,
      cell: ({ row }) =>
        row.original.originalEnrollment
          ? `${row.original.originalEnrollment.childLastName?.toUpperCase()} ${row.original.originalEnrollment.childFirstName ? StringFormatter.capitalize(row.original.originalEnrollment.childFirstName) : '-'}`
          : '-'
    },
    {
      accessorKey: 'createdAt',
      header: 'Place bloquée le',
      enableSorting: true,
      cell: ({ row }) => (
        <Badge text={DateFormatter.DDMMYYYY(row.original.createdAt)} variant="grey" />
      )
    },
    {
      accessorKey: 'createdBy.lastName',
      header: 'Login',
      enableSorting: true,
      cell: ({ row }) =>
        row.original.createdBy
          ? toFullName(row.original.createdBy.firstName, row.original.createdBy.lastName, {
              options: { mode: 'lastname-firstname' }
            })
          : '-'
    }
  ]

  return (
    <InnerBox className="rounded-3xl p-0">
      <DynamicDataTable<BlockedPlaceLight, BlockedPlaceFilters>
        ref={tableRef}
        headerType="full"
        columns={columns}
        endpoint={constructApiUrl(API.blockedPlaces.getAll.url, { id: centerId })}
        paginated={false}
        title="Places Bloquées"
        selectable
        version="LIGHT"
        label={() => null}
        filters={{ isAffected: false }}
        permissions={{ delete: false }}
        actions={[
          {
            type: 'global',
            variant: 'ghost',
            icon: LockIcon,
            label: 'Bloquer une place',
            fn: () => toggleModal('create-blocked-place')
          },
          {
            type: 'multiple',
            variant: 'destructive',
            icon: UserCanceledIcon,
            label: 'Débloquer',
            fn: (blockedPlaces) => toggleModal('delete-blocked-place', blockedPlaces)
          }
        ]}
      />

      <Modal
        icon={LockIcon}
        variant={'regular'}
        isOpen={modalState['create-blocked-place']}
        onCancel={() => closeModel('create-blocked-place')}
        title={'Bloquer une place'}
        hideActions
      >
        <BlockedPlaceForm
          centerId={centerId}
          className={MODAL_BODY_CLASSNAME}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('create-blocked-place'),
            onConfirm: async (values) => createBlockedPlaceMutation({ id: centerId, ...values })
          })}
        />
      </Modal>

      <Modal
        icon={UserCanceledIcon}
        variant={'destructive'}
        isOpen={modalState['delete-blocked-place']}
        isLoading={isDeleteBlockedPlacesPending}
        onCancel={() => closeModel('delete-blocked-place')}
        title={`Êtes-vous sûr de vouloir débloquer ${(selectedData?.length ?? 0) > 1 ? 'ces places' : 'cette place'} (${selectedBlockedPlaceNames}) ?`}
        actionButtonsOptions={{
          confirmText: 'Débloquer',
          cancelText: 'Annuler',
          onConfirm: () => {
            if (selectedData)
              deleteBlockedPlacesMutation({
                centerId,
                blockedPlaces: selectedData.map((enrollment) => enrollment.id)
              })
          },
          hideIcon: true
        }}
      />
    </InnerBox>
  )
}

export default CenterBlockedStudents
